import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { readCapabilityVocabulary, type CapabilityDefinition } from "../../../api/content/authoring/capabilityVocabularyApi";
import { useLoad } from "../../../shared/loading/useLoad";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import type { CommitBuilder } from "../../changeSets/changeSetChoice";
import AddToChangeSet from "../draft/AddToChangeSet";
import DefinitionPanel from "../draft/DefinitionPanel";
import FieldProblems from "../draft/FieldProblems";
import { itemEditAt, replacingTargetOf, type ItemTarget } from "../draft/itemEdits";
import { useDefinitionCheck, type DefinitionCheck } from "../draft/useDefinitionCheck";
import { useUnsavedDraft } from "../draft/useUnsavedDraft";
import MarkdownField from "../../../ui/forms/MarkdownField";
import Section from "../../../ui/forms/Section";
import { defaultValueOf, emptyEntry, valueOfText, type CapabilityOverrideEntry, type CapabilityOverridesContent } from "./overrideDrafts";
import Field from "../../../ui/forms/Field";
import LoadStatus from "../../../ui/feedback/LoadStatus";

export interface CapabilityOverridesBase {
  readonly reference: string;
  readonly itemId: string;
  readonly content: CapabilityOverridesContent;
}

interface CapabilityOverridesFormProps {
  projectId: string;
  /** The version the draft starts from; `null` for a new item. */
  base: CapabilityOverridesBase | null;
  /** A new item's first content, such as an entry the go-live dialog named. */
  initial: CapabilityOverridesContent;
}

/**
 * The capability overrides form (capabilities.md CAP-4): a team's assertions about what
 * models can do, each matched by route and model and checked against the vocabulary. They go
 * into a change set, so curators review them like any item, and every Thread records the
 * version it relied on.
 */
export default function CapabilityOverridesForm({ projectId, base, initial }: CapabilityOverridesFormProps) {
  const { t } = useTranslation();
  const loadVocabulary = useCallback((signal: AbortSignal) => readCapabilityVocabulary(projectId, signal), [projectId]);
  const { state: vocabulary, reload } = useLoad(loadVocabulary);
  const [draft, setDraft] = useState<CapabilityOverridesContent>(() => base?.content ?? initial);
  const [target] = useState<ItemTarget>(() => replacingTargetOf(base));

  const { connectSaver, markSaved } = useUnsavedDraft(draft);
  const check = useDefinitionCheck(projectId, draft, base === null ? null : target.itemId);
  const commits = useCallback((): readonly CommitBuilder[] => [async (head) => [await itemEditAt(projectId, head, target, draft)]], [projectId, target, draft]);

  if (vocabulary.status !== "loaded") return <LoadStatus state={vocabulary} loadingLabel={t("authoring.capabilities.loadingVocabulary")} loadingClassName="py-6" onRetry={reload} />;
  const setEntry = (index: number, entry: CapabilityOverrideEntry) => {
    setDraft({ ...draft, entries: draft.entries.map((existing, position) => (position === index ? entry : existing)) });
  };
  const blockedBy = draft.name.trim() === "" ? t("authoring.capabilities.blockedName") : draft.entries.length === 0 ? t("authoring.capabilities.blockedEntries") : null;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="space-y-4">
        <Section title={t("authoring.capabilities.about")}>
          <Field label={t("authoring.capabilities.name")}>
            {(control) => (
              <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={draft.name} maxLength={200} onChange={(event) => { setDraft({ ...draft, name: event.target.value }); }} />
            )}
          </Field>
          <FieldProblems check={check} path="name" />
          <MarkdownField label={t("authoring.capabilities.description")} value={draft.description} rows={3} onChange={(description) => { setDraft({ ...draft, description }); }} />
        </Section>
        <Section title={t("authoring.capabilities.entries")} hint={t("authoring.capabilities.entriesHint")}>
          <ul className="space-y-3">
            {draft.entries.map((entry, index) => (
              // Entries have no identity of their own; their position is what the server's problems name.
              <li key={index} className="app-card space-y-2 p-3">
                <EntryFields entry={entry} vocabulary={vocabulary.value} check={check} index={index} onChange={(next) => { setEntry(index, next); }} />
                <AppButton variant="ghost" size="xs" onClick={() => { setDraft({ ...draft, entries: draft.entries.filter((_entry, position) => position !== index) }); }}>
                  {t("authoring.capabilities.removeEntry")}
                </AppButton>
              </li>
            ))}
          </ul>
          <AppButton variant="secondary" size="sm" onClick={() => { setDraft({ ...draft, entries: [...draft.entries, emptyEntry()] }); }}>
            {t("authoring.capabilities.addEntry")}
          </AppButton>
          <FieldProblems check={check} path="entries" />
        </Section>
      </div>
      <div className="space-y-4">
        <DefinitionPanel content={draft} base={base?.content ?? null} check={check} />
        <AddToChangeSet
          ref={connectSaver}
          projectId={projectId}
          defaultTitle={t(base === null ? "authoring.capabilities.addTitle" : "authoring.capabilities.editTitle", { name: draft.name.trim() || t("authoring.capabilities.unnamed") })}
          commits={commits}
          blockedBy={blockedBy}
          onSaved={markSaved}
          notice={<p className="app-help-text text-xs">{t("authoring.capabilities.reviewNotice")}</p>}
        />
      </div>
    </div>
  );
}

interface EntryFieldsProps {
  entry: CapabilityOverrideEntry;
  vocabulary: readonly CapabilityDefinition[];
  check: DefinitionCheck;
  index: number;
  onChange: (entry: CapabilityOverrideEntry) => void;
}

function EntryFields({ entry, vocabulary, check, index, onChange }: EntryFieldsProps) {
  const { t } = useTranslation();
  const definition = vocabulary.find((candidate) => candidate.key === entry.key);
  return (
    <div className="grid gap-2 text-sm sm:grid-cols-2">
      <Field label={t("authoring.capabilities.routeKind")}>
        {(control) => (
          <select {...control} className={FORM_SELECT_CLASS} value={entry.route_kind} onChange={(event) => { onChange({ ...entry, route_kind: event.target.value === "api_key" ? "api_key" : "agent" }); }}>
            <option value="agent">{t("authoring.capabilities.routeKinds.agent")}</option>
            <option value="api_key">{t("authoring.capabilities.routeKinds.api_key")}</option>
          </select>
        )}
      </Field>
      <Field label={t(entry.route_kind === "agent" ? "authoring.capabilities.agentMatch" : "authoring.capabilities.hostMatch")}>
        {(control) => (
          <input {...control} className={`${FORM_COMPACT_CONTROL_CLASS} font-mono`} value={entry.route_match} onChange={(event) => { onChange({ ...entry, route_match: event.target.value }); }} />
        )}
      </Field>
      <Field label={t("authoring.capabilities.modelMatch")}>
        {(control) => (
          <input {...control} className={`${FORM_COMPACT_CONTROL_CLASS} font-mono`} value={entry.model_match} onChange={(event) => { onChange({ ...entry, model_match: event.target.value }); }} />
        )}
      </Field>
      <Field label={t("authoring.capabilities.key")} hint={definition?.description}>
        {(control) => (
          <select
            {...control}
            className={FORM_SELECT_CLASS}
            value={entry.key}
            onChange={(event) => {
              const chosen = vocabulary.find((candidate) => candidate.key === event.target.value);
              onChange({ ...entry, key: event.target.value, value: chosen === undefined ? entry.value : defaultValueOf(chosen.type) });
            }}
          >
            <option value="">{t("authoring.capabilities.chooseKey")}</option>
            {vocabulary.map((candidate) => <option key={candidate.key} value={candidate.key}>{candidate.key}</option>)}
          </select>
        )}
      </Field>
      <Field label={t("authoring.capabilities.value")}>
        {(control) =>
          definition?.type === "boolean" || (definition === undefined && typeof entry.value === "boolean") ? (
            <select {...control} className={FORM_SELECT_CLASS} value={String(entry.value)} onChange={(event) => { onChange({ ...entry, value: valueOfText("boolean", event.target.value) }); }}>
              <option value="true">{t("authoring.capabilities.yes")}</option>
              <option value="false">{t("authoring.capabilities.no")}</option>
            </select>
          ) : (
            <input
              {...control}
              className={FORM_COMPACT_CONTROL_CLASS}
              type={definition?.type === "integer" ? "number" : "text"}
              value={String(entry.value)}
              onChange={(event) => { onChange({ ...entry, value: valueOfText(definition?.type ?? "string", event.target.value) }); }}
            />
          )
        }
      </Field>
      <Field label={t("authoring.capabilities.note")} className="sm:col-span-2">
        {(control) => (
          <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={entry.note} maxLength={2000} placeholder={t("authoring.capabilities.notePlaceholder")} onChange={(event) => { onChange({ ...entry, note: event.target.value }); }} />
        )}
      </Field>
      <div className="sm:col-span-2">
        <FieldProblems check={check} path={`entries[${String(index)}]`} />
      </div>
    </div>
  );
}
